import { PageContainer } from '@/components/Atoms'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/Card'
import { CrossLinkTile } from '@/components/CrossLinkTile'
import { createFileRoute } from '@tanstack/react-router'
import {
  Calendar,
  CloudUpload,
  FileText,
  PauseCircle,
  Play,
  RefreshCw,
  Send,
  X,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BcpJobModal } from './-components/BcpJobModal'
import { BlobTransferModal } from './-components/BlobTransferModal'
import { EdiReprocessModal } from './-components/EdiReprocessModal'
import { LambdaControlModal } from './-components/LambdaControlModal'
import { MarkDeadFailedOrdersModal } from './-components/MarkDeadFailedOrdersModal'
import { PliByAccountModal } from './-components/PliByAccountModal'
import { ResendLambdaModal } from './-components/ResendLambdaModal'
import { SchedulerModal } from './-components/SchedulerModal'
import { SyncAccountNodModal } from './-components/SyncAccountNodModal'
import { SyncAccountSapModal } from './-components/SyncAccountSapModal'
import { SyncMaterialModal } from './-components/SyncMaterialModal'
import { SyncOrgHierModal } from './-components/SyncOrgHierModal'
import { SyncResourceNodModal } from './-components/SyncResourceNodModal'

export const Route = createFileRoute('/_auth/_modules/_app/operations/')({
  component: Operations,
})

function Operations() {
  const { t } = useTranslation('operations')
  const [isEdiReprocessModalOpen, setIsEdiReprocessModalOpen] = useState(false)
  const [isLambdaControlModalOpen, setIsLambdaControlModalOpen] = useState(false)
  const [isResendLambdaModalOpen, setIsResendLambdaModalOpen] = useState(false)
  const [isSchedulerModalOpen, setIsSchedulerModalOpen] = useState(false)
  const [isBlobTransferModalOpen, setIsBlobTransferModalOpen] = useState(false)
  const [isPliByAccountModalOpen, setIsPliByAccountModalOpen] = useState(false)
  const [isBcpJobModalOpen, setIsBcpJobModalOpen] = useState(false)
  const [isSyncAccountSapModalOpen, setIsSyncAccountSapModalOpen] = useState(false)
  const [isSyncAccountNodModalOpen, setIsSyncAccountNodModalOpen] = useState(false)
  const [isSyncOrgHierModalOpen, setIsSyncOrgHierModalOpen] = useState(false)
  const [isSyncMaterialModalOpen, setIsSyncMaterialModalOpen] = useState(false)
  const [isSyncResourceNodModalOpen, setIsSyncResourceNodModalOpen] = useState(false)
  const [isMarkDeadFailedOrdersModalOpen, setIsMarkDeadFailedOrdersModalOpen] = useState(false)

  return (
    <PageContainer className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('title')}</CardTitle>
          <CardDescription>{t('description')}</CardDescription>
        </CardHeader>

        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <CrossLinkTile
              icon={<RefreshCw />}
              title={t('ediReprocess.title')}
              linkText={t('ediReprocess.linkText')}
              onClick={() => setIsEdiReprocessModalOpen(true)}
            />
            <CrossLinkTile
              icon={<PauseCircle />}
              title={t('lambdaControl.title')}
              linkText={t('lambdaControl.linkText')}
              onClick={() => setIsLambdaControlModalOpen(true)}
            />
            <CrossLinkTile
              icon={<Send />}
              title={t('resendLambda.title')}
              linkText={t('resendLambda.linkText')}
              onClick={() => setIsResendLambdaModalOpen(true)}
            />
            <CrossLinkTile
              icon={<Calendar />}
              title={t('scheduler.title')}
              linkText={t('scheduler.linkText')}
              onClick={() => setIsSchedulerModalOpen(true)}
            />
            <CrossLinkTile
              icon={<CloudUpload />}
              title={t('blobTransfer.title')}
              linkText={t('blobTransfer.linkText')}
              onClick={() => setIsBlobTransferModalOpen(true)}
            />
            <CrossLinkTile
              icon={<FileText />}
              title={t('pliByAccount.title')}
              linkText={t('pliByAccount.linkText')}
              onClick={() => setIsPliByAccountModalOpen(true)}
            />
            <CrossLinkTile
              icon={<Play />}
              title={t('bcpJob.title')}
              linkText={t('bcpJob.linkText')}
              onClick={() => setIsBcpJobModalOpen(true)}
            />
            <CrossLinkTile
              icon={<X />}
              title={t('markDeadFailedOrders.title')}
              linkText={t('markDeadFailedOrders.linkText')}
              onClick={() => setIsMarkDeadFailedOrdersModalOpen(true)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('sync.title')}</CardTitle>
          <CardDescription>{t('sync.description')}</CardDescription>
        </CardHeader>

        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <CrossLinkTile
              title={t('syncAccountSap.title')}
              linkText={t('syncAccountSap.linkText')}
              onClick={() => setIsSyncAccountSapModalOpen(true)}
            />
            <CrossLinkTile
              title={t('syncAccountNod.title')}
              linkText={t('syncAccountNod.linkText')}
              onClick={() => setIsSyncAccountNodModalOpen(true)}
            />
            <CrossLinkTile
              title={t('syncOrgHier.title')}
              linkText={t('syncOrgHier.linkText')}
              onClick={() => setIsSyncOrgHierModalOpen(true)}
            />
            <CrossLinkTile
              title={t('syncMaterial.title')}
              linkText={t('syncMaterial.linkText')}
              onClick={() => setIsSyncMaterialModalOpen(true)}
            />
            <CrossLinkTile
              title={t('syncResourceNod.title')}
              linkText={t('syncResourceNod.linkText')}
              onClick={() => setIsSyncResourceNodModalOpen(true)}
            />
          </div>
        </CardContent>
      </Card>

      <EdiReprocessModal isOpen={isEdiReprocessModalOpen} setIsOpen={setIsEdiReprocessModalOpen} />
      <LambdaControlModal
        isOpen={isLambdaControlModalOpen}
        setIsOpen={setIsLambdaControlModalOpen}
      />
      <ResendLambdaModal isOpen={isResendLambdaModalOpen} setIsOpen={setIsResendLambdaModalOpen} />
      <SchedulerModal isOpen={isSchedulerModalOpen} setIsOpen={setIsSchedulerModalOpen} />
      <BlobTransferModal isOpen={isBlobTransferModalOpen} setIsOpen={setIsBlobTransferModalOpen} />
      <PliByAccountModal isOpen={isPliByAccountModalOpen} setIsOpen={setIsPliByAccountModalOpen} />
      <BcpJobModal isOpen={isBcpJobModalOpen} setIsOpen={setIsBcpJobModalOpen} />
      <SyncAccountSapModal
        isOpen={isSyncAccountSapModalOpen}
        setIsOpen={setIsSyncAccountSapModalOpen}
      />
      <SyncAccountNodModal
        isOpen={isSyncAccountNodModalOpen}
        setIsOpen={setIsSyncAccountNodModalOpen}
      />
      <SyncOrgHierModal isOpen={isSyncOrgHierModalOpen} setIsOpen={setIsSyncOrgHierModalOpen} />
      <SyncMaterialModal isOpen={isSyncMaterialModalOpen} setIsOpen={setIsSyncMaterialModalOpen} />
      <SyncResourceNodModal
        isOpen={isSyncResourceNodModalOpen}
        setIsOpen={setIsSyncResourceNodModalOpen}
      />
      <MarkDeadFailedOrdersModal
        isOpen={isMarkDeadFailedOrdersModalOpen}
        setIsOpen={setIsMarkDeadFailedOrdersModalOpen}
      />
    </PageContainer>
  )
}
